import {
  twoFactorDialogOpenAtom,
  twoFactorHintSeenAtom,
  userAtom,
} from '@/global-store';
import useLogoutFn from '@/hooks/useLogoutFn';
import ChangePasswordDialog from './ChangePasswordDialog';
import GenerateApiKeyDialog from './GenerateApiKeyDialog';
import ProfileEditDialog from '@/layout/Header/ProfileEditDialog';
import AttentionDot from '@/components/AttentionDot';
import MfaPill from '@/components/TwoFactor/MfaPill';
import { useFetchMfaStatus } from '@/http/queries/useFetchMfaStatus';
import { KeyRound, LogOut, RefreshCw, Shield, User } from 'lucide-react';
import { useAtom, useSetAtom } from 'jotai';
import { twoFactorOpener } from './twoFactor/TwoFactorDialog';
import { useId, useRef, useState } from 'react';
import {
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Typography,
  useTailwindBreakpoint,
} from '@eye-able/ui';
import { useInlineTranslation } from '@/i18n/hooks/useInlineTranslation';
import { T } from '@/i18n/T';
import type { BrandButtonProps } from '@/branding/useOrgBranding';

function ProfileSection({ brandProps }: { brandProps?: BrandButtonProps }) {
  const { t } = useInlineTranslation();
  const [user] = useAtom(userAtom);
  const logoutFn = useLogoutFn();
  const isDesktop = useTailwindBreakpoint('xl');

  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [generateApiKeyOpen, setGenerateApiKeyOpen] = useState(false);
  const openTwoFactor = useSetAtom(twoFactorDialogOpenAtom);
  const profileTrigger = useRef<HTMLButtonElement>(null);
  const mfaStatus = useFetchMfaStatus({ enabled: !!user });
  const activeMethods = mfaStatus.data?.methods.length ?? 0;
  const [hintSeen, setHintSeen] = useAtom(twoFactorHintSeenAtom);
  // Points at the new setting until it was opened once, and only while there is something to set up.
  const showHint =
    !hintSeen && mfaStatus.data?.available === true && activeMethods === 0;
  const headingId = useId();

  const userInfo = {
    name: {
      label: <T k={'name'} />,
      value: user?.firstName + ' ' + user?.lastName,
    },
    email: { label: <T k={'email'} />, value: user?.email },
    phone: { label: <T k={'phone'} />, value: user?.phone },
    company: { label: <T k={'company'} />, value: user?.company },
  };

  return (
    <>
      <Popover>
        <PopoverTrigger asChild>
          <Button
            ref={profileTrigger}
            type='button'
            variant='ghost'
            size={isDesktop ? 'default' : 'icon'}
            {...brandProps}
          >
            <User aria-hidden />
            <span className='eye:max-xl:sr-only'>{t('profile')}</span>
            {showHint ? (
              <AttentionDot
                label={t('twoFactorNotSetUp')}
                className='eye:max-xl:absolute eye:max-xl:top-4 eye:max-xl:right-4'
              />
            ) : null}
          </Button>
        </PopoverTrigger>
        <PopoverContent
          // lower z-index to be below other modals from this popover
          className='eye:z-1300 eye:p-s! eye:pt-m eye:max-w-[500px] eye:max-h-(--radix-popover-content-available-height)'
          align={'end'}
          matchTriggerWidth={false}
          collisionPadding={16}
          aria-labelledby={headingId}
        >
          <Typography id={headingId} tag='h2' variant='headline-s'>
            <T k='profileHeader' />
          </Typography>
          <dl className='eye:mt-2xs eye:mb-s eye:flex eye:flex-col eye:gap-3xs'>
            {Object.entries(userInfo).map(([key, { label, value }]) => (
              <div key={key}>
                <Typography tag='dt' variant='body-s-highlight'>
                  {label}
                </Typography>
                <Typography tag='dd' className='eye:break-all'>
                  {value || ' - '}
                </Typography>
              </div>
            ))}
          </dl>
          <ul className='eye:flex eye:flex-col eye:gap-2xs'>
            <li className='eye:flex eye:flex-col'>
              <ProfileEditDialog user={user} />
            </li>

            <li className='eye:flex eye:flex-col'>
              <Button
                type='button'
                variant='outline'
                className='eye:w-full'
                onClick={() => setChangePasswordOpen(true)}
              >
                <KeyRound aria-hidden />
                {t('changePassword')}
              </Button>
            </li>

            <li className='eye:flex eye:flex-col'>
              <Button
                type='button'
                variant='outline'
                className='eye:w-full'
                onClick={() => setGenerateApiKeyOpen(true)}
              >
                <RefreshCw aria-hidden />
                {t('generateApiKey')}
              </Button>
            </li>

            {mfaStatus.data?.available === false ? null : (
              <li className='eye:flex eye:flex-col'>
                <Button
                  type='button'
                  variant='outline'
                  className='eye:w-full eye:justify-between eye:px-16'
                  onClick={() => {
                    twoFactorOpener.current = profileTrigger.current;
                    setHintSeen(true);
                    openTwoFactor(true);
                  }}
                >
                  <span className='eye:flex eye:items-center eye:gap-4xs'>
                    <Shield aria-hidden />
                    {t('twoFactorAuthentication')}
                    {showHint ? <AttentionDot /> : null}
                  </span>
                  {mfaStatus.data ? (
                    <MfaPill tone={activeMethods > 0 ? 'on' : 'off'}>
                      {activeMethods > 0
                        ? t('mfaActiveCount', { count: activeMethods })
                        : t('mfaOff')}
                    </MfaPill>
                  ) : null}
                </Button>
              </li>
            )}

            <li className='eye:flex eye:flex-col'>
              <Button
                type='button'
                onClick={() => void logoutFn()}
                variant='outline'
              >
                <LogOut aria-hidden />
                {t('logOutSB')}
              </Button>
            </li>
          </ul>
        </PopoverContent>
      </Popover>
      <ChangePasswordDialog
        open={changePasswordOpen}
        onOpenChange={setChangePasswordOpen}
      />
      <GenerateApiKeyDialog
        open={generateApiKeyOpen}
        onOpenChange={setGenerateApiKeyOpen}
      />
    </>
  );
}

export default ProfileSection;
